import { Button, Descriptions, Form, Input, Modal, message } from 'antd';
import React, { useEffect } from 'react';
import { useIntl } from 'umi';
import { useAuthStore } from '@/stores/authStore';
import { isSuperadminRole } from '@/utils/permissions';
import { useUpdateAdmin } from '@/hooks/useAdmins';
import type { API } from '@/services/ant-design-pro/typings';

interface UserProfileModalProps {
  visible: boolean;
  onClose: () => void;
}

const UserProfileModal: React.FC<UserProfileModalProps> = ({
  visible,
  onClose,
}) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const { user, updateUser } = useAuthStore();
  const updateAdminMutation = useUpdateAdmin();

  // Get user data from auth store
  const userData = user ? {
    id: user._id,
    grade: isSuperadminRole(user.role)
      ? intl.formatMessage({ id: 'pages.administrators.role.superadmin' })
      : user.role === 'admin' 
      ? intl.formatMessage({ id: 'pages.administrators.role.admin' })
      : user.role,
    name: user.name,
    email: user.email,
  } : null;

  // Populate form when modal opens or user data changes
  useEffect(() => {
    if (visible && userData) {
      form.setFieldsValue({
        name: userData.name,
        email: userData.email,
      });
    }
  }, [visible, userData, form]);

  const handleSubmit = async (values: any) => {
    if (!user) {
      message.error('User information not found');
      return;
    }

    try {
      // Prepare update data - do NOT include permissions for this modal
      const updateData: API.UpdateAdminParams = {
        name: values.name,
        email: values.email,
        role: user.role, // Keep the existing role
      };

      // Only include password if it's provided
      if (values.password) {
        updateData.password = values.password;
      }

      // Note: permissions are intentionally NOT included here
      // Other pages can still use the API with permissions by passing them in the data object

      updateAdminMutation.mutate(
        { id: user._id, data: updateData },
        {
          onSuccess: (response) => {
            // Update the auth store with the response data from API
            if (response.data?.admin) {
              const updatedAdmin = response.data.admin;
              updateUser({
                name: updatedAdmin.name || values.name,
                email: updatedAdmin.email || values.email,
                role: updatedAdmin.role || user.role,
                permissions: updatedAdmin.permissions || user.permissions,
                isActive: updatedAdmin.isActive ?? user.isActive,
                updatedAt: updatedAdmin.updatedAt,
                lastLogin: updatedAdmin.lastLogin || user.lastLogin,
              });
            } else {
              // Fallback to form values if response doesn't have admin data
              updateUser({
                name: values.name,
                email: values.email,
              });
            }
            message.success(intl.formatMessage({ id: 'pages.userProfile.updateSuccess' }));
            onClose();
          },
          onError: () => {
            message.error(intl.formatMessage({ id: 'pages.userProfile.updateFailed' }));
          },
        },
      );
    } catch (error) {
      // console.error('Validation failed:', error);
    }
  };

  if (!userData) {
    return null;
  }

  return (
    <Modal
      title={intl.formatMessage({ id: 'pages.userProfile.modalTitle' })}
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {intl.formatMessage({ id: 'pages.common.cancel' })}
        </Button>,
        <Button
          key="save"
          type="primary"
          htmlType="submit"
          form="user-profile-form"
          loading={updateAdminMutation.isPending}
        >
          {intl.formatMessage({ id: 'pages.common.save' })}
        </Button>,
      ]}
      width={600}
    >
      <div style={{ marginBottom: 24 }}>
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label={intl.formatMessage({ id: 'pages.userProfile.userId' })}>{userData.id}</Descriptions.Item>
          <Descriptions.Item label={intl.formatMessage({ id: 'pages.userProfile.grade' })}>{userData.grade}</Descriptions.Item>
        </Descriptions>
      </div>

      <Form
        id="user-profile-form"
        form={form}
        layout="vertical"
        initialValues={userData}
        onFinish={handleSubmit}
      >
        <Form.Item
          label={intl.formatMessage({ id: 'pages.userProfile.name' })}
          name="name"
          rules={[{ required: true, message: intl.formatMessage({ id: 'pages.userProfile.nameRequired' }) }]}
        >
          <Input placeholder={intl.formatMessage({ id: 'pages.userProfile.namePlaceholder' })} />
        </Form.Item>

        <Form.Item
          label={intl.formatMessage({ id: 'pages.userProfile.email' })}
          name="email"
          rules={[
            { required: true, message: intl.formatMessage({ id: 'pages.userProfile.emailRequired' }) },
            { type: 'email', message: intl.formatMessage({ id: 'pages.userProfile.emailInvalid' }) },
          ]}
        >
          <Input placeholder={intl.formatMessage({ id: 'pages.userProfile.emailPlaceholder' })} />
        </Form.Item>

        {/* Phone number field hidden as requested */}
        {/* <Form.Item
          label={intl.formatMessage({ id: 'pages.userProfile.phone' })}
          name="phone"
          rules={[{ required: true, message: intl.formatMessage({ id: 'pages.userProfile.phoneRequired' }) }]}
        >
          <Input placeholder={intl.formatMessage({ id: 'pages.userProfile.phonePlaceholder' })} />
        </Form.Item> */}

        <Form.Item
          label={intl.formatMessage({ id: 'pages.userProfile.newPassword' })}
          name="password"
          rules={[{ min: 6, message: intl.formatMessage({ id: 'pages.userProfile.passwordMinLength' }) }]}
        >
          <Input.Password placeholder={intl.formatMessage({ id: 'pages.userProfile.newPasswordPlaceholder' })} />
        </Form.Item>

        <Form.Item
          label={intl.formatMessage({ id: 'pages.userProfile.confirmPassword' })}
          name="confirmPassword"
          dependencies={['password']}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(intl.formatMessage({ id: 'pages.userProfile.passwordMismatch' })),
                );
              },
            }),
          ]}
        >
          <Input.Password placeholder={intl.formatMessage({ id: 'pages.userProfile.confirmPasswordPlaceholder' })} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default UserProfileModal;
